import { getNext, getPrevious } from "@faire/slate/lib/arrayUtils";
import { useCallback } from "react";
import React from "react";

import { TabNavitaionMode } from "./types";

export enum TabNavigation {
  Next = "Next",
  Previous = "Previous",
  Select = "Select",
}

interface IOptions<T> {
  focused: T | undefined;
  items: T[];
  mode: TabNavitaionMode;
  selected: T | undefined;
  onFocus: (item: T | undefined) => void;
  onSelect: (item: T | undefined) => void;
}

export const useTabNavigation = <T>({
  focused,
  items,
  mode,
  selected,
  onFocus,
  onSelect,
}: IOptions<T>) => {
  const keyboardHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (items.length === 0) {
        return;
      }

      const onFocusOrSelected =
        mode === "manual"
          ? onFocus
          : (item: T | undefined) => {
              onFocus(item);
              onSelect(item);
            };

      switch (event.key) {
        case KeyboardKey.ArrowRight: {
          const next = getNext(focused ?? selected, items);
          onFocusOrSelected(next);
          return;
        }
        case KeyboardKey.ArrowLeft: {
          const previous = getPrevious(focused ?? selected, items);
          onFocusOrSelected(previous);
          return;
        }
        case KeyboardKey.Home: {
          onFocusOrSelected(items[0]);
          return;
        }
        case KeyboardKey.End: {
          onFocusOrSelected(items[items.length - 1]);
          return;
        }
        case KeyboardKey.Enter:
        case KeyboardKey.Space: {
          onSelect(focused ?? selected);
          return;
        }
        default:
          return;
      }
    },
    [focused, items, mode, onFocus, onSelect, selected]
  );

  return { keyboardHandler };
};

enum KeyboardKey {
  ArrowRight = "ArrowRight",
  ArrowLeft = "ArrowLeft",
  Enter = "Enter",
  Space = "Space",
  Home = "Home",
  End = "End",
}
